import { FOOTPRINT_CELL_SPAN } from "../document/footprint-cells.ts";
import { MAX_DEPTH_LINES, MAX_DEPTH_RECTANGLES, type CellBox, type DepthLine, type LineDepth } from "./frame-depth.ts";

/**
 * The one check of a frame's depth (ADR-098 decision 11), called wherever a depth is written or read: a
 * route, a pull, the compile and a panel. A depth that breaks a rule is refused with the first rule broken, by
 * name; nothing is clamped or repaired.
 */

/**
 * The rule a depth is refused by. `shape`: the value is not `{ floor?, lines? }` of rectangles of four whole
 * numbers. `lines`: more than `MAX_DEPTH_LINES` lines. `rectangles`: more than `MAX_DEPTH_RECTANGLES`
 * rectangles over the floor and the lines together. `size`: a rectangle with a negative corner or without a
 * positive size. `span`: a rectangle reaching a coordinate of `FOOTPRINT_CELL_SPAN` or more. `up`: a line's
 * height that is not a whole number from 0 to the frame's height. `sameUp`: two lines at one height, which
 * are one line. `overlap`: two rectangles sharing a cell, within a kind or across kinds. `outside`: a
 * rectangle past the frame's cell grid.
 */
export type DepthRule = "shape" | "lines" | "rectangles" | "size" | "span" | "up" | "sameUp" | "overlap" | "outside";

/**
 * The frame a depth is checked against: its size in pixels and the pixels of one collision cell.
 */
export interface DepthFrameSize {
  readonly w: number;
  readonly h: number;
  readonly cellPixels: number;
}

/**
 * What the check answers: the depth, copied with its known fields alone, or the first rule broken and a
 * message naming the path and the value.
 */
export type LineDepthResult = { readonly ok: true; readonly depth: LineDepth } | { readonly ok: false; readonly rule: DepthRule; readonly message: string };

/**
 * A refusal.
 *
 * @param rule The rule broken.
 * @param message What is wrong, naming the path.
 * @returns The result.
 */
function refuse(rule: DepthRule, message: string): LineDepthResult {
  return { ok: false, rule, message };
}

/**
 * Whether a value is a plain object holding no key but the ones named.
 *
 * @param value The value.
 * @param keys The keys it may hold.
 * @returns True when it is.
 */
function isRecordOf(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.keys(value).every((key) => keys.includes(key));
}

/**
 * Reads a list of rectangles of four whole numbers.
 *
 * @param value The value.
 * @returns The rectangles, copied, or null when the value is not such a list.
 */
function boxesOf(value: unknown): CellBox[] | null {
  if (!Array.isArray(value) || !value.every((box) => Array.isArray(box) && box.length === 4 && box.every((n) => typeof n === "number" && Number.isInteger(n)))) {
    return null;
  }
  return (value as number[][]).map((box) => [box[0] as number, box[1] as number, box[2] as number, box[3] as number]);
}

/**
 * Reads the shape of a depth, with no rule applied but that it is one.
 *
 * @param value The value.
 * @returns The depth, copied, or the path of the first part that is not of the shape.
 */
function shapeOf(value: unknown): { readonly depth: LineDepth } | { readonly at: string } {
  if (!isRecordOf(value, ["floor", "lines"])) {
    return { at: "depth" };
  }
  const floor = value["floor"] === undefined ? undefined : boxesOf(value["floor"]);
  if (floor === null) {
    return { at: "floor" };
  }
  const listed = value["lines"];
  if (listed !== undefined && !Array.isArray(listed)) {
    return { at: "lines" };
  }
  const lines: DepthLine[] = [];
  for (const [i, line] of ((listed as unknown[] | undefined) ?? []).entries()) {
    const cells = isRecordOf(line, ["up", "cells"]) && typeof line["up"] === "number" ? boxesOf(line["cells"]) : null;
    if (cells === null) {
      return { at: `lines[${i}]` };
    }
    lines.push({ up: (line as { up: number }).up, cells });
  }
  return { depth: { ...(floor === undefined ? {} : { floor }), ...(listed === undefined ? {} : { lines }) } };
}

/**
 * Whether two rectangles share a cell.
 *
 * @param a One rectangle.
 * @param b The other.
 * @returns True when they overlap.
 */
function overlaps(a: CellBox, b: CellBox): boolean {
  return a[0] < b[0] + b[2] && b[0] < a[0] + a[2] && a[1] < b[1] + b[3] && b[1] < a[1] + a[3];
}

/**
 * Checks a value as a frame's depth (ADR-098 decision 11). Without a frame the rules that need none are
 * applied: the shape, the counts, each rectangle's size and span, each line's height from 0, no two lines at
 * one height and no overlap. With a frame the rest are too: a line's height at most the frame's, every
 * rectangle inside the frame's cell grid (a last column or top row the frame only partly covers is inside).
 *
 * @param value The candidate depth.
 * @param frame The frame it belongs to, or undefined where its size is not known.
 * @returns The depth, or the first rule broken with its message.
 */
export function validateLineDepth(value: unknown, frame?: DepthFrameSize): LineDepthResult {
  const shape = shapeOf(value);
  if ("at" in shape) {
    return refuse("shape", `${shape.at} must be ${shape.at === "depth" ? "an object of floor and lines alone" : shape.at === "lines" ? "a list of lines" : shape.at === "floor" ? "[[column, rowUp, columns, rows], ...] in whole numbers" : "{ up, cells: [[column, rowUp, columns, rows], ...] } in whole numbers"}`);
  }
  const { depth } = shape;
  const lines = depth.lines ?? [];
  if (lines.length > MAX_DEPTH_LINES) {
    return refuse("lines", `lines holds ${lines.length} lines and a frame takes at most ${MAX_DEPTH_LINES}`);
  }
  const boxes: { readonly box: CellBox; readonly at: string }[] = [...(depth.floor ?? []).map((box, i) => ({ box, at: `floor[${i}]` })), ...lines.flatMap((line, l) => line.cells.map((box, i) => ({ box, at: `lines[${l}].cells[${i}]` })))];
  if (boxes.length > MAX_DEPTH_RECTANGLES) {
    return refuse("rectangles", `depth holds ${boxes.length} rectangles and a frame takes at most ${MAX_DEPTH_RECTANGLES}`);
  }
  for (const { box, at } of boxes) {
    if (box[0] < 0 || box[1] < 0 || box[2] < 1 || box[3] < 1) {
      return refuse("size", `${at} [${box.join(", ")}] must start at 0 or more and span at least one column and one row`);
    }
  }
  for (const { box, at } of boxes) {
    if (box[0] + box[2] > FOOTPRINT_CELL_SPAN || box[1] + box[3] > FOOTPRINT_CELL_SPAN) {
      return refuse("span", `${at} [${box.join(", ")}] takes cell coordinates under ${FOOTPRINT_CELL_SPAN}`);
    }
  }
  for (const [l, line] of lines.entries()) {
    if (!Number.isInteger(line.up) || line.up < 0 || (frame !== undefined && line.up > frame.h)) {
      return refuse("up", `lines[${l}].up ${line.up} must be a whole number from 0${frame === undefined ? "" : ` to ${frame.h}, the frame's height`}`);
    }
  }
  for (const [l, line] of lines.entries()) {
    const earlier = lines.findIndex((other) => other.up === line.up);
    if (earlier < l) {
      return refuse("sameUp", `lines[${earlier}] and lines[${l}] are both ${line.up} px up, which is one line`);
    }
  }
  for (const [i, a] of boxes.entries()) {
    const other = boxes.slice(0, i).find((b) => overlaps(a.box, b.box));
    if (other !== undefined) {
      return refuse("overlap", `${other.at} and ${a.at} overlap`);
    }
  }
  if (frame === undefined) {
    return { ok: true, depth };
  }
  const columns = Math.ceil(frame.w / frame.cellPixels);
  const rows = Math.ceil(frame.h / frame.cellPixels);
  for (const { box, at } of boxes) {
    if (box[0] + box[2] > columns || box[1] + box[3] > rows) {
      return refuse("outside", `${at} [${box.join(", ")}] lies outside the frame's ${columns} by ${rows} cells`);
    }
  }
  return { ok: true, depth };
}
